import { apiRequest, isNetworkError } from "../api/client";

/**
 * Seller identity.
 *
 * Canon APP-001: "No seller data is trusted solely from URL/client state." This module is
 * the one place seller identity enters the app, and it has exactly one source —
 * `GET /v1/seller/me`, answered against the HttpOnly session cookie. Nothing about a
 * seller is ever read from the address bar, from local storage, or from the redirect that
 * ended a sign-in: `contracts/API.md` § Seller sign-in shape gives that redirect "no
 * identity, no token, no marker" precisely so this browser has one source for the fact.
 *
 * The response is parsed defensively rather than cast: a browser can be pointed at any
 * host, so a body that does not match the contract is treated as no session at all.
 */

export interface Seller {
  id: string;
  displayName: string;
  /**
   * Free-form in V0 on purpose. `contracts/DOMAIN.md` gives the seller a status without
   * fixing its vocabulary, and CORE-006 will settle it; until then the app renders what
   * core says rather than inventing an enum it would have to keep in step.
   */
  status: string;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** Returns null when the body is not a seller, so a malformed answer can never sign anyone in. */
export function parseSeller(body: unknown): Seller | null {
  if (typeof body !== "object" || body === null) return null;
  const seller = (body as { seller?: unknown }).seller;
  if (typeof seller !== "object" || seller === null) return null;

  const record = seller as Record<string, unknown>;
  const id = nonEmptyString(record.id);
  const displayName = nonEmptyString(record.displayName);
  const status = nonEmptyString(record.status);
  if (!id || !displayName || !status) return null;

  return { id, displayName, status };
}

/** The signed-in seller, or null when core does not recognise this browser's session. */
export async function fetchSeller(): Promise<Seller | null> {
  return parseSeller(await apiRequest<unknown>("/v1/seller/me"));
}

/**
 * Ends the session, and reports whether the request actually landed.
 *
 * `contracts/API.md` § Seller session shape: `POST /v1/seller/session/logout` "answers
 * `204` and clears the cookie whether or not a session was found — signing out must never
 * fail". That promise is core's, and it holds for every request that *reaches* core. It
 * cannot cover a request that never left the browser.
 *
 * This resolves either way rather than throwing, because a transport failure is not a fault
 * the caller should have to catch. But it returns `false` in that case, and the caller has
 * to do something with it: the session cookie is `HttpOnly`, so a browser that could not
 * reach core has not ended anything, and the founder is still signed in. Showing them the
 * signed-out surface would be a comfortable lie told to exactly the person the control
 * exists for — someone on a borrowed laptop who is about to walk away from it.
 *
 * There is nothing to revoke beyond the cookie. The founder's Google account signs straight
 * back in, and under `D-008` there is no mailed credential left behind to worry about.
 *
 * Only a *transport* failure counts as undelivered. An HTTP answer of any kind — including
 * the `401` core gives a session it no longer honours — means the request arrived and the
 * cookie is spent, so those resolve `true`. Reporting a delivered `401` as a failed
 * sign-out would be the same lie in reverse: telling a founder they are still signed in
 * when they are not.
 *
 * @returns `true` when core answered, `false` when the request could not be delivered.
 */
export async function endSession(): Promise<boolean> {
  try {
    await apiRequest("/v1/seller/session/logout", { method: "POST" });
    return true;
  } catch (error) {
    // Deliberately not logged: the error can carry request detail. The boolean is all the
    // caller needs, and it is the part that changes what the founder should be shown.
    return !isNetworkError(error);
  }
}
